<script setup lang="ts">
/**
 * Listado de recibos (§31).
 *
 * Portado del **invoice list** de la plantilla
 * (`pages/apps/invoice/list/index.vue`): tira de widgets, tarjeta de filtros,
 * `VDataTableServer` y las acciones por fila. Lo que cambia es el vocabulario —
 * acá no se manda nada ni se espera a nadie, se cobra en el mostrador— así que
 * no hay "sent", "overdue" ni "balance": tres estados y un total.
 */
import { type ClientPayload, createClient, listClients } from '@/api/clients'
import type { DiveCenterClient } from '@/types'
import {
  type Receipt,
  type ReceiptStatus,
  listReceipts,
} from '@/api/receipts'
import AlertBox from '@/components/AlertBox.vue'
import { useCurrentDiveCenter } from '@/composables/useCurrentDiveCenter'
import { usePreferences } from '@/composables/usePreferences'
import { apiMessage, notifyError, notifySuccess } from '@/composables/useSnackbar'

definePage({ meta: { requiresAuth: true } })

const router = useRouter()
const { currentId } = useCurrentDiveCenter()
const { formatCurrency, formatDate } = usePreferences()

const receipts = ref<Receipt[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref('')

const search = ref('')
const statusFilter = ref<ReceiptStatus | null>(null)
const page = ref(1)
const itemsPerPage = ref(25)

const STATUS_CHIP: Record<ReceiptStatus, { label: string; color: string; icon: string }> = {
  draft: { label: 'Draft', color: 'secondary', icon: 'ri-draft-line' },
  paid: { label: 'Paid', color: 'success', icon: 'ri-checkbox-circle-line' },
  void: { label: 'Void', color: 'error', icon: 'ri-close-circle-line' },
}

const headers = [
  { title: '#', key: 'number' },
  { title: 'Client', key: 'clientName' },
  { title: 'Issued', key: 'createdAt' },
  { title: 'Status', key: 'status', sortable: false },
  { title: 'Total', key: 'total', align: 'end' as const },
  { title: '', key: 'actions', sortable: false, width: 120, align: 'end' as const },
]

/** Los contadores de la tira, por estado. */
const counts = computed(() => ({
  draft: receipts.value.filter(r => r.status === 'draft').length,
  paid: receipts.value.filter(r => r.status === 'paid').length,
  void: receipts.value.filter(r => r.status === 'void').length,
}))

async function load() {
  const dcId = currentId.value

  if (!dcId)
    return

  loading.value = true
  error.value = ''
  try {
    const res = await listReceipts(dcId, {
      status: statusFilter.value ?? undefined,
      search: search.value.trim() || undefined,
      limit: itemsPerPage.value,
      skip: (page.value - 1) * itemsPerPage.value,
    })

    receipts.value = res.receipts
    total.value = res.total
  }
  catch (e) {
    error.value = apiMessage(e, 'Could not load receipts.')
    receipts.value = []
  }
  finally {
    loading.value = false
  }
}

onMounted(load)

/*
 * Es una pantalla de la CUENTA en el menú pero sus datos son del centro, y el
 * `:key` del RouterView no la remonta al cambiar de centro desde la cabecera
 * (§23.3): sin este watch se quedaría mostrando los recibos del anterior.
 */
watch(currentId, load)

// Buscar y filtrar vuelven a la primera página: quedarse en la 3 de un
// resultado que ahora tiene una sola página muestra una tabla vacía.
watch([search, statusFilter], () => {
  page.value = 1
  load()
})

watch([page, itemsPerPage], load)

// ---- Alta ----

const newDialog = ref(false)
const clientSearch = ref('')
const clients = ref<DiveCenterClient[]>([])
const clientsLoading = ref(false)

let clientTimer: number | undefined

watch(clientSearch, q => {
  if (clientTimer)
    clearTimeout(clientTimer)

  // Mismo debounce que el buscador de la libreta.
  clientTimer = window.setTimeout(async () => {
    clientsLoading.value = true
    try {
      const res = await listClients({ search: q?.trim() || undefined, limit: 20 })

      clients.value = res.clients
    }
    finally {
      clientsLoading.value = false
    }
  }, 350)
})

/**
 * El alta rápida de ficha, dentro del mismo diálogo.
 *
 * El mismo bloque que el de presupuestos: cobrarle a alguien que acaba de
 * aparecer en el mostrador no puede obligar a ir a la libreta, crear la ficha y
 * volver — ese es el paso que se olvida. Son los cuatro campos del walk-in del
 * roster; el resto se completa cuando haga falta.
 */
const creating = ref(false)
const savingClient = ref(false)

const newClient = reactive<Pick<ClientPayload, 'firstName' | 'lastName' | 'email' | 'phone'>>({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
})

/*
 * El nombre es lo único obligatorio, y lo dice el propio campo: un botón apagado
 * no explica qué falta.
 */
const clientFormRef = ref()
const requiredRule = (v: string) => (!!v && !!v.trim()) || 'Required.'

async function createAndStart() {
  const { valid } = await clientFormRef.value.validate()

  if (!valid)
    return

  savingClient.value = true
  try {
    const { client } = await createClient({
      firstName: newClient.firstName.trim(),
      lastName: newClient.lastName.trim(),
      email: newClient.email?.trim() || undefined,
      phone: newClient.phone?.trim() || undefined,

      // Queda en el centro donde se está cobrando.
      diveCenterIds: currentId.value ? [currentId.value] : undefined,
    })

    notifySuccess('Client added.')
    startReceipt(client)
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not create the client.'))
  }
  finally {
    savingClient.value = false
  }
}

function openNew() {
  clientSearch.value = ''
  clients.value = []
  creating.value = false
  newClient.firstName = ''
  newClient.lastName = ''
  newClient.email = ''
  newClient.phone = ''
  newDialog.value = true

  // Se dispara la primera carga sin escribir nada: la libreta entera es el
  // punto de partida, igual que en el picker de clientes del roster (§20.10).
  clientSearch.value = ' '
  clientSearch.value = ''
}

/**
 * Abre el editor con ese cliente, **sin crear nada**.
 *
 * Antes el recibo se creaba acá, al elegir: equivocarse de cliente ya dejaba un
 * documento en el listado y no había forma de deshacerlo. Ahora el borrador vive
 * en memoria hasta que se guarda, y el correlativo se reserva recién al cobrar.
 */
function startReceipt(client: DiveCenterClient) {
  newDialog.value = false
  router.push(`/sales/receipts/new?clientId=${client.id}`)
}

const money = (amount: number, currency: string) => formatCurrency(amount, currency)

const clientLabel = (c: DiveCenterClient) => `${c.firstName} ${c.lastName ?? ''}`.trim()

/*
 * Un borrador se abre para SEGUIR ARMÁNDOLO; uno cobrado o anulado, para
 * mirarlo. Mandar los dos al mismo lado obligaría a uno de los dos casos a dar
 * un rodeo, y el frecuente es el que no debería darlo.
 */
const receiptTo = (r: Receipt) =>
  r.status === 'draft' ? `/sales/receipts/${r.id}` : `/sales/receipts/${r.id}/preview`
</script>

<template>
  <div>
    <AlertBox
      variant="danger"
      :message="error"
    />

    <!-- Tira de contadores, el mismo bloque del listado de clientes. -->
    <VCard class="mb-6">
      <VCardText class="d-flex flex-wrap ga-6">
        <div
          v-for="s in (['draft', 'paid', 'void'] as ReceiptStatus[])"
          :key="s"
          class="d-flex align-center ga-3"
        >
          <VAvatar
            :color="STATUS_CHIP[s].color"
            variant="tonal"
            rounded
          >
            <VIcon :icon="STATUS_CHIP[s].icon" />
          </VAvatar>
          <div>
            <div class="text-h6">
              {{ counts[s] }}
            </div>
            <div class="text-caption text-medium-emphasis">
              {{ STATUS_CHIP[s].label }}
            </div>
          </div>
        </div>
      </VCardText>
    </VCard>

    <VCard>
      <!--
        El mismo orden que el listado de clientes: los filtros a la izquierda, y
        contra el margen derecho el buscador y el alta. El botón estaba suelto
        arriba de la tarjeta, que es un renglón entero de pantalla para un solo
        botón y otra convención más que aprender.
      -->
      <VCardText class="d-flex flex-wrap align-center ga-4">
        <VSelect
          v-model="statusFilter"
          :items="[
            { title: 'All', value: null },
            { title: 'Draft', value: 'draft' },
            { title: 'Paid', value: 'paid' },
            { title: 'Void', value: 'void' },
          ]"
          label="Status"
          density="compact"
          hide-details
          style="min-inline-size: 200px; max-inline-size: 240px;"
        />

        <VSpacer />

        <VTextField
          v-model="search"
          placeholder="Search by number or client"
          prepend-inner-icon="ri-search-line"
          density="compact"
          clearable
          hide-details
          style="min-inline-size: 200px; max-inline-size: 280px;"
        />

        <VBtn
          prepend-icon="ri-add-line"
          @click="openNew"
        >
          New receipt
        </VBtn>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        :items="receipts"
        :items-length="total"
        :headers="headers"
        :loading="loading"
        item-value="id"
        class="text-no-wrap"
        @update:options="() => {}"
      >
        <template #item.number="{ item }">
          <!--
            Un borrador todavía no tiene número: se reserva al cobrar. Se
            marca con un guión en vez de dejar la celda vacía, que se lee como
            un dato que no cargó.
          -->
          <RouterLink
            :to="receiptTo(item)"
            class="font-weight-medium"
          >
            {{ item.number ?? '—' }}
          </RouterLink>
        </template>

        <template #item.createdAt="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>

        <template #item.status="{ item }">
          <VChip
            :color="STATUS_CHIP[item.status].color"
            size="small"
          >
            {{ STATUS_CHIP[item.status].label }}
          </VChip>
        </template>

        <template #item.total="{ item }">
          <span class="font-weight-medium">{{ money(item.total, item.currency) }}</span>
        </template>

        <template #item.actions="{ item }">
          <!--
            Una sola acción: abrir. El PDF vive en el preview, que es la
            pantalla donde se decide qué hacer con el comprobante.
          -->
          <VBtn
            icon="ri-eye-line"
            variant="text"
            size="small"
            :to="receiptTo(item)"
            title="Open"
          />
        </template>

        <template #no-data>
          <div class="text-center text-medium-emphasis py-8">
            No receipts yet.
          </div>
        </template>
      </VDataTableServer>
    </VCard>

    <!-- Alta: elegir el cliente y listo. Lo que debe se trae solo. -->
    <VDialog
      v-model="newDialog"
      max-width="560"
      scrollable
    >
      <VCard>
        <VCardItem>
          <VCardTitle>New receipt</VCardTitle>
          <VCardSubtitle>
            Everything this diver hasn't paid for yet comes in automatically.
          </VCardSubtitle>
        </VCardItem>

        <VDivider />

        <VCardText v-if="!creating">
          <VTextField
            v-model="clientSearch"
            placeholder="Search the client book"
            prepend-inner-icon="ri-search-line"
            clearable
            :loading="clientsLoading"
            class="mb-4"
          />

          <VList v-if="clients.length">
            <VListItem
              v-for="c in clients"
              :key="c.id"
              :title="clientLabel(c)"
              :subtitle="c.email || undefined"
              @click="startReceipt(c)"
            >
              <template #append>
                <VIcon icon="ri-arrow-right-line" />
              </template>
            </VListItem>
          </VList>

          <p
            v-else-if="!clientsLoading"
            class="text-body-2 text-medium-emphasis"
          >
            No clients found.
          </p>

          <VBtn
            variant="tonal"
            prepend-icon="ri-user-add-line"
            class="mt-2"
            @click="creating = true"
          >
            New client
          </VBtn>
        </VCardText>

        <!--
          🪤 `pt-6`: el diálogo es `scrollable`, así que el contenido se recorta
          contra la cabecera y el label flotante del primer campo quedaba pisado.
          Mismo cepo que el `VWindow` de §3.7 — un label que flota hacia arriba
          necesita aire arriba.
        -->
        <VForm
          v-else
          ref="clientFormRef"
          @submit.prevent="createAndStart"
        >
          <VCardText class="pt-6">
            <VRow>
              <VCol
                cols="12"
                sm="6"
              >
                <VTextField
                  v-model="newClient.firstName"
                  label="First name"
                  :rules="[requiredRule]"
                  autofocus
                />
              </VCol>
              <VCol
                cols="12"
                sm="6"
              >
                <VTextField
                  v-model="newClient.lastName"
                  label="Last name"
                />
              </VCol>
              <VCol
                cols="12"
                sm="6"
              >
                <VTextField
                  v-model="newClient.email"
                  label="Email"
                  type="email"
                  hint="Used to send the receipt."
                  persistent-hint
                />
              </VCol>
              <VCol
                cols="12"
                sm="6"
              >
                <VTextField
                  v-model="newClient.phone"
                  label="Phone"
                />
              </VCol>
            </VRow>
          </VCardText>

          <!--
            Enter en cualquier campo crea y sigue: son cuatro campos, y llevar
            la mano al botón es un gesto de más en el flujo que vino a
            acortar. El que se ve es el de las acciones del diálogo.
          -->
          <VBtn
            type="submit"
            class="d-none"
          />
        </VForm>

        <VDivider />

        <VCardActions class="justify-end">
          <VBtn
            variant="text"
            @click="creating ? (creating = false) : (newDialog = false)"
          >
            {{ creating ? 'Back' : 'Cancel' }}
          </VBtn>
          <VBtn
            v-if="creating"
            :loading="savingClient"
            @click="createAndStart"
          >
            Create and continue
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
